import React, { useEffect, useState, useMemo } from "react";
import Grid from "@mui/material/Grid";
import { Button, CircularProgress, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import ProviderDetailsForm from "../../molecules/Providers/ProviderDetailsForm";
import ProviderAccountList from "../../molecules/Providers/ProviderAccountList";
import {
  addProviderFromAccount,
  getProviderDetail,
  updateProviderFromAccount,
} from "../../api/providers";
import { getCatalogue, getTaxRegimeList } from "../../api/common/catalogues.ts";
import * as Yup from "yup";
import { useSnack } from "../../services/SnakbarProvider.js";
import { handleError } from "../../services/errorHandler.js";

// Move these to a separate types file later
interface ProviderFormProps {
  companyId: number;
  onSubmit: () => void;
  id?: number;
}

type FormMode = "edit" | "insert";

// Payloads (mirror backend ProviderDtos.cs) for /from-account endpoints
interface ProviderFormInsertPayload {
  provider: {
    legalName: string;
    code: string;
    taxId: string;
    providerScopeId: number;
    legalEntityTypeId: number;
    regimeId?: number | null;
    subregimeId?: number | null;
    corporationTypeId?: number | null;
    descripcion?: string | null;
    website?: string | null;
    creditDays: number;
    requestedBy: string;
  };
  accounts: Array<{
    companyId?: number;
    contpaqiAccountNo: string;
    description: string;
    isDefault: boolean;
  }>;
}

interface ProviderFormEditPayload {
  provider: {
    companyId: number;
    code: string;
    taxId?: string | null;
    legalName?: string | null;
    providerScopeId?: number | null;
    legalEntityTypeId?: number | null;
    regimeId?: number | null;
    corporationTypeId?: number | null;
    descripcion?: string | null;
    website?: string | null;
    creditDays?: number | null;
    requestedBy?: string | null;
  };
  accounts: Array<{
    id: number;
    companyId: number;
    contpaqiAccountNo: string;
    description: string;
    isDefault: boolean;
  }>;
}

const initialValues = {
  provider: {
    legalName: "",
    code: "",
    taxId: "",
    providerScopeId: "",
    legalEntityTypeId: "",
    regimeId: "",
    corporationTypeId: "",
    descripcion: "",
    website: "",
    creditDays: 0,
    requestedBy: "",
  },
  accounts: [],
};

const PLACEHOLDER_CONTPAQI_ACCOUNT_NO = "0000-0000-0000";
const PLACEHOLDER_ACCOUNT_DESCRIPTION = "POR DEFINIR";

const accountItemSchema = Yup.object().shape({
  contpaqiAccountNo: Yup.string()
    .required("La cuenta contable es requerida.")
    .matches(
      /^\d{4}-\d{4}-\d{4}$/,
      "El formato debe ser XXXX-XXXX-XXXX (12 dígitos con guiones)"
    ),
  description: Yup.string().required("La descripción es requerida."),
});

const resolveAccountsForSubmit = (
  accounts: Array<{ contpaqiAccountNo?: string; description?: string; isDefault?: boolean }>,
  formMode: FormMode
) => {
  const hasFilledAccount =
    accounts?.length > 0 &&
    accounts.some((a) => a.contpaqiAccountNo?.trim());

  if (formMode === "insert" && !hasFilledAccount) {
    return [
      {
        contpaqiAccountNo: PLACEHOLDER_CONTPAQI_ACCOUNT_NO,
        description: PLACEHOLDER_ACCOUNT_DESCRIPTION,
        isDefault: true,
      },
    ];
  }

  return accounts;
};

const validationSchema = Yup.object().shape({
  provider: Yup.object().shape({
    legalName: Yup.string().required("El nombre legal es obligatorio"),
    code: Yup.string().required("El código es obligatorio"),
    taxId: Yup.string().required("El ID fiscal es obligatorio"),
    providerScopeId: Yup.number()
      .required("El Origen del proveedor es obligatorio")
      .integer(),
    legalEntityTypeId: Yup.number().integer(),
    regimeId: Yup.number().integer().nullable(),
    corporationTypeId: Yup.number().nullable(),
    descripcion: Yup.string(),
    website: Yup.string().url("URL inválida"),
    creditDays: Yup.number()
      .required("Los días de crédito son obligatorios")
      .integer(),
  }),
  accounts: Yup.array().of(accountItemSchema),
});

const ProviderForm = ({ companyId, onSubmit, id }: ProviderFormProps) => {
  const formMode: FormMode = id ? "edit" : "insert";
  const [formData, setFormData] = useState(initialValues);
  const [loading, setLoading] = useState(false);
  const { openSnack } = useSnack();
  const [isPersonaMoral, setIsPersonaMoral] = useState(false);
  const [taxRegimes, setTaxRegimes] = useState<any[]>([]);
  const [selectedProviderTypeId, setSelectedProviderTypeId] = useState<number | null>(null);


  const handleSubmit = async (values) => {
    setLoading(true);
    
    // Check if providerOrigin is EXTRANJERO and legalEntityType is PERSONA MORAL
    // If so, clear corporationTypeId and regimeId from the payload
    let shouldClearForeignFields = false;
    if (values.provider.providerScopeId && values.provider.legalEntityTypeId) {
      try {
        const [providerOriginsRes, providerTypesRes] = await Promise.all([
          getCatalogue({ CatalogueName: "PROVIDER_ORIGIN" }),
          getCatalogue({ CatalogueName: "PROVIDER_TYPE" }),
        ]);
        
        const providerScope = providerOriginsRes.data.find(
          (item) => item.id === values.provider.providerScopeId
        );
        const legalEntityType = providerTypesRes.data.find(
          (item) => item.id === values.provider.legalEntityTypeId
        );
        
        shouldClearForeignFields =
          providerScope?.name === "EXTRANJERO" &&
          legalEntityType?.code === "PM";
      } catch (error) {
        // If fetching catalogues fails, continue with submission
        console.error("Error fetching catalogues:", error);
      }
    }
    
    const resolvedAccounts = resolveAccountsForSubmit(values.accounts, formMode);

    if (formMode === "insert") {
      try{
        const { providerScopeId, ...providerRest } = values.provider;
        const payloadInsert: ProviderFormInsertPayload = {
          provider: {
            ...providerRest,
            // Backend CreateProviderDto: ProviderOriginId
            providerScopeId,
            ...(shouldClearForeignFields && {
              corporationTypeId: null,
              regimeId: null,
            }),
          },
          accounts: resolvedAccounts.map((e) => ({ ...e, companyId })),
        };

        await addProviderFromAccount(payloadInsert);
        openSnack("Proveedor guardado exitosamente", "success");
        onSubmit();
      }catch(e){
        openSnack(handleError(e), "error");
      }
    } else {
      try{
        const payloadEdit: ProviderFormEditPayload = {
          provider: {
            companyId,
            code: values.provider.code,
            taxId: values.provider.taxId,
            legalName: values.provider.legalName,
            providerScopeId: values.provider.providerScopeId,
            legalEntityTypeId: values.provider.legalEntityTypeId,
            ...(shouldClearForeignFields
              ? { corporationTypeId: null, regimeId: null }
              : {
                  corporationTypeId: values.provider.corporationTypeId,
                  regimeId: values.provider.regimeId,
                }),
            descripcion: values.provider.descripcion,
            website: values.provider.website,
            creditDays: values.provider.creditDays,
            requestedBy: values.provider.requestedBy,
          },
          accounts: resolvedAccounts.map((e) => ({ ...e, companyId })),
        };

        await updateProviderFromAccount(id, payloadEdit);
        openSnack("Proveedor actualizado exitosamente", "success");
        onSubmit();
      }catch(e){
        openSnack(handleError(e), "error");
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (formMode === "edit") {
        const res = await getProviderDetail(id, companyId);
        setFormData({
          provider: res.data,
          accounts: res.data.accounts,
        });
        setIsPersonaMoral(res.data.legalEntityType?.code === "PM");
        setSelectedProviderTypeId(res.data.legalEntityTypeId || null);
      } else {
        setFormData(initialValues);
        setSelectedProviderTypeId(null);
      }
    };

    fetchData();
  }, [formMode, id, companyId]);

  // Memoize tax regime list based on selected provider type
  useEffect(() => {
    const fetchTaxRegimes = async () => {
      if (selectedProviderTypeId) {
        try {
          const res = await getTaxRegimeList({ providerTypeId: selectedProviderTypeId });
          setTaxRegimes(res.data || []);
        } catch (error) {
          console.error("Error fetching tax regimes:", error);
          setTaxRegimes([]);
        }
      } else {
        setTaxRegimes([]);
      }
    };

    fetchTaxRegimes();
  }, [selectedProviderTypeId]);

  // Memoize the tax regime list to prevent unnecessary re-renders
  const memoizedTaxRegimes = useMemo<any[]>(() => taxRegimes, [taxRegimes]);

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Formik
      initialValues={formData}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, errors, touched, setFieldValue }) => (
        <Form id={id?.toString()}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography pb={2} pt={4} variant="h4">
                Datos Generales
              </Typography>
              <ProviderDetailsForm 
                errors={errors} 
                touched={touched} 
                isPersonaMoral={isPersonaMoral}
                taxRegimes={memoizedTaxRegimes}
                onProviderTypeChange={setSelectedProviderTypeId}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h4" pb={2} pt={4}>
                Cuentas Contables
              </Typography>
              <ProviderAccountList
                values={values}
                errors={errors}
                touched={touched}
                setFieldValue={setFieldValue}
                formMode={formMode}
              />
            </Grid>

            <Grid item xs={12}>
              <Button variant="contained" color="primary" type="submit" disabled={Object.keys(errors).length > 0}>
                Guardar
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default ProviderForm;
